import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem } from '../../../../../components/LeftMenu/MenuItem';
import { ReportsNav } from './ReportsNav';
import {
  useCreativeAnalyticsGet,
  useCreativeAnalyticsSet,
} from '../../../contexts';
import { DashboardIcon } from '../../icons/dashboard';
import { useDispatch, useSelector } from 'react-redux';
import { trackEvent } from '../../../../../store/UserActivity/actions';
import {
  CREATIVE_ANALYTICS,
  CREATIVE_ANALYTICS,
  TOGGLE_ADSET_REPORT,
  TOGGLE_BLOCK_REPORT,
  TOGGLE_COPILOT,
  TOGGLE_COPY,
  TOGGLE_DASHBOARD,
  TOGGLE_VISUAL_MODE,
} from '../../../../../store/UserActivity/constants';
import {
  COPILOT,
  COPY,
  DASHBOARD,
  PERFORMANCE_FREQUENCY,
  RECOMMENDATIONS,
  REPORT_BUILDER,
} from '../../../../../store/CreativeReports/constants';
import { TrendingIcon } from '../../icons/trending';
import { LegacyReports } from './LegacyReports';
import { BetaReports } from './BetaReports';
import { usePermissions } from '../../../../../components/Permissions/usePermissions';
import {
  LEGACY_REPORTS,
  BETA_REPORTS,
  TRENDING,
  COPY_REPORT,
  ANNOTATE_VISUALS,
  SETTINGS,
  DRAFT_CREATIVE,
  BLOCK_REPORT,
  ROADMAP,
  ANALYTICS,
  STUDIO,
  PFR,
} from '../../../../../components/Permissions/constants';
import { useSearchParams } from '../../../../../components/Hooks/search-params';
import { useSetShowQaEditor, useUrlFeature } from './hooks';
import RoleAccess from '../../../../../components/RoleAccess';
import { CopyIcon } from '../../icons/copy';
import { hasRoles, isAdminRole } from '../../../../../cookies';
import { DraftCreativeIcon } from '../../icons/draftCreative';
import Tooltip from '../../../../../components/Tooltip';
import {
  AnalyticsIcon,
  RoadmapIcon,
  StudioIcon,
} from '../../../../../components/Nav/assets';
import { useIsDirectMail, useViewId } from '../../../selectors';
import { BlockIcon } from '../../icons/block';
import { useHistory } from 'react-router-dom';
import {
  setIsSettingsOpen,
  setShowQaEditor,
} from '../../../../../store/CreativeAnalytics/creativeAnalyticsSlice';
import { EasyAiIcon } from '../../../../EasyAiManager/Sidebar';
import ReportModal from '../../../../Analytics2/components/ReportModal';
import { getAnalyticsUrl } from '../../../../Analytics2/utils/getAnalyticsUrl';
import { useNotifications } from '../../../../../components/Notification/useNotifications';

const TableModeMenu = ({
  collapsed,
  setCollapsed,
  loading = false,
  savedReportsApi,
}) => {
  const dispatch = useDispatch();
  const { getParams, setParams: setSearchParams } = useSearchParams();
  const { report_id } = getParams('report_id');
  const hasPermission = usePermissions();
  const setShowQaEditor = useSetShowQaEditor();
  const history = useHistory();
  const { feature, page } = useUrlFeature();
  const isAnalytics = feature === 'analytics';
  const isStudio = feature === 'studio';
  const isStudioNestedRoute =
    page === 'draft-creative' || feature === 'trending';

  const allowStudio = hasPermission(STUDIO);
  const allowPFR = hasPermission(PFR);
  const view_id = useViewId();
  const [savedReports, setSavedReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState({
    name: '',
    description: '',
    url: '',
    report_id: '',
  });
  const [showReportModal, setShowReportModal] = useState(false);
  const [useCurrentParams, setUseCurrentParams] = useState(false);
  const [reportCurrentlyEditing, setReportCurrentlyEditing] = useState(null);
  const notifications = useNotifications();

  const toggleUseParams = () => {
    setUseCurrentParams((prev) => !prev);
  };

  const fetchReports = () => {
    savedReportsApi.fetch.request({ view_id });
  };

  const getDomain = (str, sub) => {
    const index = str.indexOf(sub);
    if (index === -1) {
      return str; // Substring not found, return the original string
    }
    return str.substring(0, index);
  };

  useEffect(() => {
    if (report_id && savedReports.length > 0) {
      const foundReport = savedReports.find(
        (report) => report.report_id === report_id
      );

      if (foundReport) {
        setSelectedReport(foundReport);
      }
    }
  }, [report_id, savedReports]);

  const handleSelectReport = (report) => {
    const { report_id, url } = report;
    savedReportsApi.get.request({ report_id });

    const newUrl = `${getDomain(
      window.location.href,
      view_id
    )}${url}&report_id=${report.report_id}`;

    window.location.replace(newUrl);
  };

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (savedReportsApi.fetch.data) {
      const { reports } = savedReportsApi.fetch.data;

      if (reports && reports.length > 0) {
        setSavedReports(reports);
      }
    }
  }, [savedReportsApi.fetch.data]);

  const saveStatus = {
    loading: savedReportsApi.post.loading,
    error: savedReportsApi.post.error,
    success: savedReportsApi.post.data,
  };

  const updateStatus = {
    loading: savedReportsApi.update.loading,
    error: savedReportsApi.update.error,
    success: savedReportsApi.update.data,
  };

  const deleteStatus = {
    loading: savedReportsApi.delete.loading,
    error: savedReportsApi.delete.error,
    success: savedReportsApi.delete.data,
  };

  const handleSaveReport = () => {
    const urlParams = getAnalyticsUrl(window.location.href, view_id);

    const payload = {
      url: useCurrentParams
        ? `${view_id}${urlParams}`
        : reportCurrentlyEditing.url,
      name: reportCurrentlyEditing.name,
      description: reportCurrentlyEditing.description,
      report_id: reportCurrentlyEditing.report_id,
    };

    savedReportsApi.update.request(payload);
  };

  const handleDeleteReport = async () => {
    const payload = {
      view_id,
      report_id: reportCurrentlyEditing.report_id,
    };

    const conf = await confirm('Delete this report?');

    if (conf) {
      savedReportsApi.delete.request(payload);
    }
  };

  useEffect(() => {
    if (updateStatus.success) {
      savedReportsApi.fetch.request({ view_id });

      notifications.add({
        id: 'udpate-toast',
        title: 'Success!',
        message: 'Report saved.',
        toastProps: {
          autoClose: 3000,
          hideProgressBar: false,
        },
      });

      setShowReportModal(false);
    }
  }, [updateStatus.success]);

  useEffect(() => {
    if (deleteStatus.success) {
      savedReportsApi.fetch.request({ view_id });

      notifications.add({
        id: 'delete-toast',
        title: 'Report successfully deleted.',
        toastProps: {
          autoClose: 3000,
          hideProgressBar: false,
        },
      });

      setShowReportModal(false);
    }
  }, [deleteStatus.success]);

  const {
    darwin_light,
    allowedAdmin,
    isTikTokView,
    isDirectMail,
    viewName,
    isOrganicView,
    viewId,
  } = useSelector(
    ({
      ViewSettings: {
        darwin_light,
        allowedAdmin,
        isTikTokView,
        isDirectMail,
        viewName,
        isOrganicView,
        viewId,
      },
    }) => ({
      darwin_light,
      allowedAdmin,
      isTikTokView,
      isDirectMail,
      viewName,
      isOrganicView,
      viewId,
    })
  );

  const prefixView = (rest) => `/${viewId}/${rest}`;

  const isCompetitorView =
    isDirectMail && viewName.toLowerCase().includes('competitor');

  const itemClass = collapsed ? 'icon-only' : '';

  const handleSelection = (key, subkey = null) => {
    const modes = ['analytics', 'library'];

    const withParams = !!subkey ? `${key}/${subkey}` : key;

    history.push(prefixView(withParams));
  };

  return (
    <>
      {showReportModal && (
        <ReportModal
          isEditMode={true}
          report={reportCurrentlyEditing}
          setReport={setReportCurrentlyEditing}
          {...{
            setShowReportModal,
            handleSaveReport,
            saveStatus,
            updateStatus,
            deleteStatus,
            useCurrentParams,
            setUseCurrentParams,
            handleDeleteReport,
          }}
        />
      )}
      <MenuItem
        title="Creative Analytics"
        icon={
          <AnalyticsIcon
            isSelected={isAnalytics && !page}
            // className="menu-item__icon"
          />
        }
        onMenuItemClick={() => {
          // setSearchParams({ display: 'analytics', category: '' });
          setCollapsed(false);
          handleSelection('analytics');
        }}
        isSelected={isAnalytics && !page}
        className={itemClass}
        isPrimaryItem
        // exclude={(!isNonPaidMediaType && !isUltraLight) || isOrganicView}
        subMenu={
          <>
            <MenuItem
              title={'Saved Reports'}
              exclude={!isAnalytics || collapsed || page === DRAFT_CREATIVE}
              className="detailed-report-item"
              {...{ collapsed, setCollapsed }}
              subMenu={
                <>
                  {savedReports.length > 0 ? (
                    savedReports.map((report) => {
                      return (
                        <MenuItem
                          title={report.name}
                          onMenuItemClick={() => {
                            handleSelectReport(report);
                          }}
                          isSelected={
                            report.report_id === selectedReport.report_id
                          }
                          isSavedReport={true}
                          className="saved-report-item"
                          onEditItemClick={() => {
                            setReportCurrentlyEditing(report);
                            setShowReportModal(true);
                          }}
                        />
                      );
                    })
                  ) : (
                    <div>No saved reports.</div>
                  )}
                </>
              }
            />
            <MenuItem
              title={'BETA Reports'}
              className="detailed-report-item"
              exclude={!isAnalytics || page === DRAFT_CREATIVE}
              subMenu={
                <>
                  <MenuItem
                    title="Copy Report"
                    onMenuItemClick={() => {
                      dispatch(trackEvent(CREATIVE_ANALYTICS, TOGGLE_COPY));

                      handleSelection('analytics', 'copy');
                    }}
                    isSelected={isAnalytics && page === 'copy'}
                    exclude={
                      isTikTokView ||
                      isOrganicView ||
                      isCompetitorView ||
                      isDirectMail ||
                      !hasPermission(COPY_REPORT)
                    }
                    className={itemClass}
                    {...{ collapsed, setCollapsed }}
                  />

                  <Tooltip
                    disabled={!collapsed}
                    content={'Block Report'}
                    placement={'right'}
                    follow={false}
                  >
                    <div>
                      <MenuItem
                        title="Block Report"
                        // icon={<BlockIcon />}
                        id="block-report"
                        onMenuItemClick={() => {
                          dispatch(
                            trackEvent(CREATIVE_ANALYTICS, TOGGLE_BLOCK_REPORT)
                          );
                          handleSelection('analytics', 'block_report');
                        }}
                        isSelected={isAnalytics && page === 'block_report'}
                        exclude={
                          !hasPermission(BLOCK_REPORT) ||
                          isCompetitorView ||
                          ((darwin_light || isDirectMail) && !allowedAdmin) ||
                          isOrganicView
                        }
                        className={itemClass}
                      />
                    </div>
                  </Tooltip>
                </>
              }
            />

            <MenuItem
              title={'Legacy Reports'}
              className="detailed-report-item"
              exclude={
                !hasPermission(LEGACY_REPORTS) ||
                !isAnalytics ||
                page === DRAFT_CREATIVE
              }
              subMenu={
                <>
                  <MenuItem
                    title="Creative Performance"
                    onMenuItemClick={() => {
                      handleSelection('analytics', 'dna_table');
                    }}
                    isSelected={isAnalytics && page === 'dna_table'}
                    className={itemClass}
                    {...{ collapsed, setCollapsed }}
                  />

                  <ReportsNav
                    loading={loading}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    handleSelection={handleSelection}
                    feature={feature}
                    page={page}
                  />
                </>
              }
            />
          </>
        }
        {...{ collapsed, setCollapsed }}
      />

      <MenuItem
        title="Creative Studio"
        icon={<StudioIcon isSelected={isStudio} />}
        onMenuItemClick={() => {
          // setSearchParams({ display: 'dna_table', category: '' });
          setCollapsed(true);
          handleSelection('studio');
        }}
        isSelected={isStudio}
        className={'detailed-report-item'}
        isPrimaryItem
        exclude={!allowStudio}
        {...{ collapsed, setCollapsed }}
        subMenu={
          <>
            <MenuItem
              title="Creative Equations"
              icon={<DraftCreativeIcon />}
              isSelected={page === DRAFT_CREATIVE}
              onMenuItemClick={() =>
                handleSelection('analytics', DRAFT_CREATIVE)
              }
              exclude={
                (!isStudio && !isStudioNestedRoute) ||
                !hasPermission(DRAFT_CREATIVE)
              }
              className={itemClass}
              {...{ collapsed }}
            />
            <MenuItem
              title="Trending Library"
              icon={<TrendingIcon isSelected={feature === 'trending'} />}
              onMenuItemClick={() => {
                setCollapsed(true);
                handleSelection('trending');
              }}
              isSelected={feature === 'trending'}
              className={'detailed-report-item'}
              exclude={(!isStudio && !isStudioNestedRoute) || !allowStudio}
              {...{ collapsed, setCollapsed }}
            />
          </>
        }
      />

      <MenuItem
        title="Creative Roadmap"
        icon={<i class="fa-solid fa-route"></i>}
        onMenuItemClick={() => {
          handleSelection('roadmap');
          setCollapsed(true);
        }}
        isSelected={feature === 'roadmap'}
        className={itemClass}
        isPrimaryItem
        {...{ collapsed, setCollapsed }}
      />

      <MenuItem
        title="Easy AI Editor"
        icon={<EasyAiIcon />}
        onMenuItemClick={() => {
          handleSelection('easy-ai-manager');
          setCollapsed(true);
        }}
        isSelected={feature === 'easy-ai-manager'}
        className={itemClass}
        isPrimaryItem
        {...{ collapsed, setCollapsed }}
        exclude={!allowPFR || !hasRoles(['internal'])}
      />
      {/* <LegacyReports
        {...{
          loading,
          isDirectMail,
          // isNonPaidMediaType,
          darwin_light,
          allowedAdmin,
          isCompetitorView,
          itemClass,
          collapsed,
        }}
        exclude={
          !hasPermission(LEGACY_REPORTS) ||
          isUltraLight ||
          viewName.includes('Email')
        }
      /> */}
      {/* <BetaReports
        {...{
          loading,
          isDirectMail,
          // isNonPaidMediaType,
          darwin_light,
          allowedAdmin,
          isCompetitorView,
          itemClass,
          collapsed,
        }}
        exclude={
          !hasPermission(BETA_REPORTS) ||
          isUltraLight ||
          isOrganicView ||
          viewName.includes('Email')
        }
      /> */}
      {/* <div className={`divider ${collapsed ? 'collapsed' : ''}`}></div> */}
      {/* <MenuItem
        title="Annotate visuals"
        icon={<i className="fa-regular fa-pen-to-square"></i>}
        onMenuItemClick={() => setShowQaEditor(true)}
        exclude={!hasPermission(ANNOTATE_VISUALS) && !isAdminRole()}
        className={itemClass}
        {...{ collapsed }}
      /> */}
      <MenuItem
        title={'Internal Tools'}
        icon={<i class="fa-solid fa-wrench"></i>}
        exclude={!isAnalytics || collapsed}
        className="detailed-report-item"
        isPrimaryItem
        {...{ collapsed, setCollapsed }}
        subMenu={
          <>
            <MenuItem
              title="Annotate visuals"
              onMenuItemClick={() => setShowQaEditor(true)}
              exclude={
                !isAnalytics ||
                !page ||
                (!hasPermission(ANNOTATE_VISUALS) && !isAdminRole())
              }
              className={itemClass}
              {...{ collapsed }}
            />
          </>
        }
      />

      <MenuItem
        title="Settings"
        icon={<i className="fa fa-gear"></i>}
        onMenuItemClick={() => dispatch(setIsSettingsOpen(true))}
        exclude={
          !isAnalytics || !page || (!hasPermission(SETTINGS) && !isAdminRole())
        }
        className={itemClass}
        isPrimaryItem
        {...{ collapsed }}
      />
    </>
  );
};

export default TableModeMenu;
